import React, { useState } from 'react'
import {
  UserIcon,
  UsersIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CheckBadgeIcon,
  HomeIcon,
  PencilIcon,
  PrinterIcon,
  QrCodeIcon,
  IdentificationIcon,
  XMarkIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import QRCodeGenerator from '../QRCodeGenerator'
import PrintModal from './PrintModal'
import {
  getResidentPhotoUrl,
  getResidentInitials,
} from '../../../utils/photoUtils'

// Utility to capitalize strings
const capitalize = (str) =>
  typeof str === 'string' && str.length > 0
    ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    : ''

const getPhotoUrl = (resident) => {
  return getResidentPhotoUrl(resident)
}

const formatAddress = (resident) => {
  if (!resident) return 'Address not provided'
  return (
    [
      resident.house_number,
      resident.street,
      resident.purok_zone,
      resident.barangay,
      resident.municipality,
      resident.province,
      resident.zip_code,
    ]
      .filter(Boolean)
      .join(', ') ||
    resident.address ||
    'Address not provided'
  )
}

export const ResidentProfileContent = ({
  resident,
  onEdit,
  onPrint,
  hideAyudaTransactions = false,
  hideProfileActions = false,
}) => {
  const householdMembers = Array.isArray(resident?.household_members)
    ? resident.household_members
    : []

  const ayudaPrograms = Array.isArray(resident?.ayuda_programs)
    ? resident.ayuda_programs
    : []

  return (
    <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
      {/* Photo and Digital ID Section */}
      <div className='lg:col-span-1 space-y-6'>
        {/* Profile Photo */}
        <div className='text-center'>
          <div className='w-64 h-64 mx-auto bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden shadow-lg'>
            {getPhotoUrl(resident) ? (
              <img
                src={getPhotoUrl(resident)}
                alt={`${resident.first_name} ${resident.last_name}`}
                className='w-full h-full object-cover'
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.parentNode.querySelector(
                    '.fallback-avatar'
                  ).style.display = 'flex'
                }}
              />
            ) : null}
            <div
              className={`fallback-avatar w-full h-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-6xl ${
                getPhotoUrl(resident) ? 'hidden' : 'flex'
              }`}
            >
              {getResidentInitials(resident)}
            </div>
          </div>
          <h2 className='text-2xl font-bold text-gray-900 mt-4'>
            {capitalize(resident.first_name)} {capitalize(resident.middle_name)}{' '}
            {capitalize(resident.last_name)} {resident.suffix}
          </h2>
          <p className='text-gray-600 font-medium'>
            {resident.occupation || 'N/A'}
          </p>
        </div>

        {!hideProfileActions ? (
          <div className='bg-gray-50 rounded-xl p-6'>
            <h4 className='text-lg font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-200 pb-2'>
              <IdentificationIcon className='w-5 h-5 mr-2 text-blue-600' />
              Family Members
            </h4>
            {householdMembers.length > 0 ? (
              <div className='space-y-3 mb-2'>
                <p className='text-xs text-gray-500 mb-2'>
                  Found {householdMembers.length} family member(s)
                </p>
                {householdMembers.map((member) => (
                  <div
                    key={member.id || Math.random()}
                    className='flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg'
                  >
                    <div className='flex items-center space-x-3'>
                      {getPhotoUrl(member) ? (
                        <img
                          src={getPhotoUrl(member)}
                          alt={`${member.first_name} ${member.last_name}`}
                          className='h-12 w-12 rounded-full object-cover border-2 border-blue-200 hover:scale-110 transition-transform duration-200'
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.parentNode.querySelector(
                              '.fallback-avatar'
                            ).style.display = 'flex'
                          }}
                        />
                      ) : (
                        <div className='w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm'>
                          {member.name
                            ? member.name
                                .split(' ')
                                .map((n) => n.charAt(0))
                                .join('')
                            : member.first_name && member.last_name
                            ? member.first_name.charAt(0) +
                              member.last_name.charAt(0)
                            : 'N/A'}
                        </div>
                      )}
                      <div>
                        <p className='font-medium text-gray-900'>
                          {member.name ||
                            `${member.first_name || ''} ${
                              member.last_name || ''
                            }`.trim() ||
                            'Unnamed Member'}
                        </p>
                        <p className='text-sm text-gray-600'>
                          {member.relationship_to_head || 'Unknown'} •{' '}
                          {member.age || 'N/A'} years old
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-center py-4 border-2 border-dashed border-gray-300 rounded-lg'>
                <UsersIcon className='mx-auto h-8 w-8 text-gray-400' />
                <p className='text-sm text-gray-500 mt-2'>
                  No family members added yet
                </p>
              </div>
            )}
          </div>
        ) : null}

        {!hideProfileActions ? (
          <div className='flex justify-end gap-4'>
            <button
              onClick={onPrint}
              className='flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200'
            >
              <PrinterIcon className='w-4 h-4' />
              <span>Print Options</span>
            </button>
            <button
              onClick={onEdit}
              className='flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200'
            >
              <PencilIcon className='w-4 h-4' />
              <span>Edit Profile</span>
            </button>
          </div>
        ) : null}
      </div>

      {/* Personal Information */}
      <div className='lg:col-span-2 space-y-6'>
        {/* Basic Information */}
        <div className='bg-gray-50 rounded-xl p-6'>
          <h4 className='text-lg font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-200 pb-2'>
            <UserIcon className='w-5 h-5 mr-2 text-blue-600' />
            Personal Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <p className='text-sm font-medium text-gray-500'>Full Name</p>
              <p className='text-gray-900 font-medium'>
                {resident.first_name} {resident.middle_name}{' '}
                {resident.last_name} {resident.suffix}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Date of Birth</p>
              <p className='text-gray-900'>
                {resident.date_of_birth || resident.birthdate
                  ? new Date(
                      resident.date_of_birth || resident.birthdate
                    ).toLocaleDateString()
                  : 'Not provided'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Birthplace</p>
              <p className='text-gray-900'>
                {resident.birthplace || 'Not provided'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Gender</p>
              <p className='text-gray-900 capitalize'>{resident.gender}</p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Civil Status</p>
              <p className='text-gray-900 capitalize'>
                {resident.civil_status || 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Citizenship</p>
              <p className='text-gray-900'>
                {resident.citizenship || 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Religion</p>
              <p className='text-gray-900'>
                {resident.religion || 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Occupation</p>
              <p className='text-gray-900'>
                {resident.occupation || 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Monthly Income
              </p>
              <p className='text-gray-900'>
                {resident.monthly_income
                  ? `₱${parseFloat(resident.monthly_income).toLocaleString()}`
                  : 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Education Level
              </p>
              <p className='text-gray-900 capitalize'>
                {resident.education_level?.replace('_', ' ') || 'Not specified'}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className='bg-gray-50 rounded-xl p-6'>
          <h4 className='text-lg font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-200 pb-2'>
            <PhoneIcon className='w-5 h-5 mr-2 text-blue-600' />
            Contact Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <p className='text-sm font-medium text-gray-500'>Phone Number</p>
              <p className='text-gray-900'>
                {resident.phone_number ||
                  resident.contact_number ||
                  'Not provided'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Email</p>
              <p className='text-gray-900'>
                {resident.email || 'Not provided'}
              </p>
            </div>
            <div className='md:col-span-2'>
              <p className='text-sm font-medium text-gray-500'>
                Complete Address
              </p>
              <p className='text-gray-900'>{formatAddress(resident)}</p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Emergency Contact Person
              </p>
              <p className='text-gray-900'>
                {resident.emergency_contact_name || 'Not provided'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Emergency Contact Number
              </p>
              <p className='text-gray-900'>
                {resident.emergency_contact_number || 'Not provided'}
              </p>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className='bg-gray-50 rounded-xl p-6'>
          <h4 className='text-lg font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-200 pb-2'>
            <CheckBadgeIcon className='w-5 h-5 mr-2 text-blue-600' />
            Voter information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <p className='text-sm font-medium text-gray-500'>Voter Status</p>
              <p className='text-gray-900 capitalize'>
                {resident.voter_status?.replace('_', ' ') || 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Precinct Number
              </p>
              <p className='text-gray-900'>
                {resident.voter?.precinct_name ||
                  resident?.precinct_number ||
                  'Not specified'}
              </p>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className='bg-gray-50 rounded-xl p-6'>
          <h4 className='text-lg font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-200 pb-2'>
            <CheckBadgeIcon className='w-5 h-5 mr-2 text-blue-600' />
            Additional Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Length of Residency
              </p>
              <p className='text-gray-900'>
                {resident.length_of_residency
                  ? `${resident.length_of_residency} years`
                  : 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Indigenous Group
              </p>
              <p className='text-gray-900'>
                {resident.indigenous_group || 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>4Ps Number</p>
              <p className='text-gray-900'>
                {resident.four_ps_number || 'Not specified'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Solo Parent</p>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  resident.solo_parent_status
                    ? 'bg-pink-100 text-pink-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {resident.solo_parent_status ? 'Yes' : 'No'}
              </span>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Person with Disability
              </p>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  resident.pwd_status
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {resident.pwd_status ? 'Yes' : 'No'}
              </span>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Senior Citizen
              </p>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  resident.senior_citizen_status
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {resident.senior_citizen_status ? 'Yes' : 'No'}
              </span>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>Illiterate</p>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  resident.illiterate_status
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {resident.illiterate_status ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>

        {/* Household Information */}
        <div className='bg-gray-50 rounded-xl p-6'>
          <h4 className='text-lg font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-200 pb-2'>
            <HomeIcon className='w-5 h-5 mr-2 text-blue-600' />
            Household Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <p className='text-sm font-medium text-gray-500'>Household ID</p>
              <p className='text-gray-900 font-mono'>
                {resident.household_number || 'Not assigned'}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Relationship to Head
              </p>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  resident.is_household_head
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {resident.is_household_head
                  ? 'Head of Household'
                  : resident.relationship_to_head || 'Member'}
              </span>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Registration Status
              </p>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  resident.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : resident.status === 'verified'
                    ? 'bg-blue-100 text-blue-800'
                    : resident.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {resident.status}
              </span>
            </div>
            <div>
              <p className='text-sm font-medium text-gray-500'>
                Digital ID Issued
              </p>
              <p className='text-gray-900'>
                {resident.digital_id_issued_at
                  ? new Date(resident.digital_id_issued_at).toLocaleDateString()
                  : 'Not issued'}
              </p>
            </div>
          </div>
        </div>

        {/* Transactions */}
        {!hideAyudaTransactions ? (
          <div className='bg-gray-50 rounded-xl p-6'>
            <div className='border-b border-gray-200 pb-2 '>
              <h4 className='text-lg font-semibold text-gray-900 flex items-center '>
                <BanknotesIcon className='w-5 h-5 mr-2 text-blue-600' />
                Ayuda Program Transactions
              </h4>

              <p className='text-gray-500  text-xs '>
                Wallet ID:{''}
                <a
                  href={resident.wallet_url}
                  target='_blank'
                  className='hover:underline hover:text-blue-400 hover:cursor-pointer'
                >
                  <span className='ml-1'>{resident.wallet_address}</span>
                </a>
              </p>
            </div>
            {ayudaPrograms.length > 0 ? (
              <>
                {ayudaPrograms.map((item, index) => (
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-center mb-3 border-b-2 py-4 px-3'>
                    <div>
                      <p className='text-lg'>{item.ayuda_program?.title}</p>
                      <p className='text-sm text-gray-900'>
                        {new Date(
                          item.status == 'received'
                            ? item.updated_at
                            : item.created_at
                        ).toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div>
                        <p className='text-sm font-medium text-gray-500'>
                          Amount
                        </p>
                        <p className='text-gray-900 capitalize font-bold'>
                          P {item.ayuda_program.amount.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-gray-500'>
                          Status
                        </p>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.status == 'received'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-300 text-gray-800'
                          }`}
                        >
                          {item.status == 'received' ? 'Received' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <p className='text-gray-900'>Not yet included in a program</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export const ResidentProfileView = ({
  resident,
  onClose,
  onEdit,
  isModal = false,
  hideAyudaTransactions = false,
  hideProfileActions = false,
}) => {
  const [showPrintModal, setShowPrintModal] = useState(false)

  if (!resident) return null

  const qrData = {
    id: resident.id,
    barangayId: resident.barangay_id_number,
    name: `${resident.first_name} ${resident.last_name}`,
    birthdate: resident.date_of_birth,
    address: formatAddress(resident),
    contactNumber: resident.phone_number || resident.contact_number,
    emergencyContact: resident.emergency_contact_name,
    emergencyContactNumber: resident.emergency_contact_number,
  }

  const content = (
    <>
      {isModal ? (
        <div className='flex justify-between items-center mb-6'>
          <h3 className='text-2xl font-bold text-gray-900 flex items-center'>
            <UserIcon className='w-7 h-7 mr-2 text-blue-600' />
            Resident Profile
          </h3>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 text-3xl transition-all duration-300 hover:scale-110'
          >
            <XMarkIcon className='w-8 h-8' />
          </button>
        </div>
      ) : null}

      <ResidentProfileContent
        resident={resident}
        onEdit={onEdit}
        onPrint={() => setShowPrintModal(true)}
        hideAyudaTransactions={hideAyudaTransactions}
        hideProfileActions={hideProfileActions}
      />

      <div className='hidden'>
        <QRCodeGenerator value={JSON.stringify(qrData)} />
      </div>

      <PrintModal
        resident={resident}
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
      />
    </>
  )

  if (!isModal) {
    return (
      <div className='p-6 bg-white rounded-lg shadow-sm border border-gray-200'>
        {content}
      </div>
    )
  }

  return (
    <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 '>
      <div className='relative top-10 mx-auto p-6 border w-11/12 max-w-6xl shadow-2xl rounded-lg bg-white'>
        {content}
      </div>
    </div>
  )
}

const ViewResidentModal = ({ resident, isOpen, onClose, onEdit }) => {
  if (!isOpen || !resident) return null

  return (
    <ResidentProfileView
      resident={resident}
      onClose={onClose}
      onEdit={onEdit}
      isModal
    />
  )
}

export default ViewResidentModal
